import { MenuItem, TextField } from '@mui/material';
import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { countActiveFilterValues } from '../../shared/filter/activeFilterCount';
import { ListFilterPanel } from '../../shared/filter/ListFilterPanel';
import type { DashboardFilters as DashboardFilterValues } from './types';

export interface DashboardFiltersProps {
  readonly value: DashboardFilterValues;
  readonly resetValue: DashboardFilterValues;
  readonly onApply: (filters: DashboardFilterValues) => void;
  readonly lockedPartnerId?: string | null;
  readonly lockedAgentId?: string | null;
}

export function DashboardFilters({
  value,
  resetValue,
  onApply,
  lockedPartnerId = null,
  lockedAgentId = null,
}: DashboardFiltersProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState(value);

  useEffect(() => setDraft(value), [value]);

  const set = (field: keyof DashboardFilterValues, nextValue: string) => {
    setDraft((current) => ({ ...current, [field]: nextValue }));
  };

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onApply({
      ...draft,
      partnerId: lockedPartnerId ?? draft.partnerId.trim(),
      agentId: lockedAgentId ?? draft.agentId.trim(),
      gameTypeId: draft.gameTypeId.trim(),
      gameId: draft.gameId.trim(),
      currency: draft.currency.trim().toUpperCase(),
      status: draft.status.trim().toUpperCase(),
    });
  };

  return (
    <ListFilterPanel
      activeSecondaryFilterCount={countActiveFilterValues(
        {
          partnerId: value.partnerId,
          agentId: value.agentId,
          gameTypeId: value.gameTypeId,
          gameId: value.gameId,
        },
        {
          partnerId: resetValue.partnerId,
          agentId: resetValue.agentId,
          gameTypeId: '',
          gameId: '',
        },
      )}
      applyLabel={t('actions.apply')}
      ariaLabel={t('filterPanel.label', { page: t('dashboard.title') })}
      clearLabel={t('dashboard.filters.reset')}
      onClear={() => {
        setDraft(resetValue);
        onApply(resetValue);
      }}
      onSubmit={submit}
      primaryFields={
        <>
          <TextField
            label={t('filters.dateFrom')}
            onChange={(event) => set('from', toBangkokTimestamp(event.target.value))}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            type="date"
            value={toDateInput(draft.from)}
          />
          <TextField
            helperText={t('dashboard.filters.toExclusive')}
            label={t('filters.dateTo')}
            onChange={(event) => set('to', toBangkokTimestamp(event.target.value))}
            size="small"
            slotProps={{ inputLabel: { shrink: true } }}
            type="date"
            value={toDateInput(draft.to)}
          />
          <TextField
            label={t('dashboard.filters.currency')}
            onChange={(event) => set('currency', event.target.value)}
            select
            size="small"
            value={draft.currency}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="THB">THB</MenuItem>
            <MenuItem value="USD">USD</MenuItem>
          </TextField>
          <TextField
            label={t('dashboard.filters.transactionStatus')}
            onChange={(event) => set('status', event.target.value)}
            select
            size="small"
            value={draft.status}
          >
            <MenuItem value="">{t('common.all')}</MenuItem>
            <MenuItem value="CALCULATED">{t('dashboard.status.calculated')}</MenuItem>
            <MenuItem value="REJECTED">{t('dashboard.status.rejected')}</MenuItem>
            <MenuItem value="FAILED">{t('status.failed')}</MenuItem>
          </TextField>
        </>
      }
      secondaryFields={
        <>
          <TextField
            disabled={lockedPartnerId !== null}
            label={t('filters.partner')}
            onChange={(event) => set('partnerId', event.target.value)}
            size="small"
            value={lockedPartnerId ?? draft.partnerId}
          />
          <TextField
            disabled={lockedAgentId !== null}
            label={t('filters.agent')}
            onChange={(event) => set('agentId', event.target.value)}
            size="small"
            value={lockedAgentId ?? draft.agentId}
          />
          <TextField
            label={t('dashboard.filters.gameTypeId')}
            onChange={(event) => set('gameTypeId', event.target.value)}
            size="small"
            value={draft.gameTypeId}
          />
          <TextField
            label={t('dashboard.filters.gameId')}
            onChange={(event) => set('gameId', event.target.value)}
            size="small"
            value={draft.gameId}
          />
        </>
      }
      sx={{ mb: 3 }}
    />
  );
}

function toDateInput(timestamp: string): string {
  return timestamp.slice(0, 10);
}

function toBangkokTimestamp(date: string): string {
  return date.length === 0 ? '' : `${date}T00:00:00+07:00`;
}
